/** Presentation only: database values and API payloads remain ISO dates. */
export function formatDisplayDate(
  value: string | Date,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const date =
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const displayOptions = { ...options };
  delete displayOptions.dateStyle;
  delete displayOptions.timeStyle;
  const parts = new Intl.DateTimeFormat('en-GB', {
    ...displayOptions,
    calendar: 'gregory',
    numberingSystem: 'latn',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  const result = `${part('day')}/${part('month')}/${part('year')}`;
  return options.weekday ? `${part('weekday')}, ${result}` : result;
}

export function isoToDateInput(value: string, withTime = false): string {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}:\d{2}))?/.exec(value);
  return match
    ? `${match[3]}/${match[2]}/${match[1]}${withTime ? ` ${match[4] ?? '00:00'}` : ''}`
    : '';
}

/** Strict day-first parsing; never use Date.parse on a slash-formatted date. */
export function dateInputToIso(value: string, withTime = false): string | null {
  if (!value.trim()) return '';
  const match = (
    withTime ? /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/ : /^(\d{2})\/(\d{2})\/(\d{4})$/
  ).exec(value);
  if (!match) return null;
  const [, dd, mm, yyyy, hh = '00', min = '00'] = match;
  const date = new Date(0);
  date.setFullYear(Number(yyyy), Number(mm) - 1, Number(dd));
  if (
    Number(yyyy) < 1 ||
    date.getFullYear() !== Number(yyyy) ||
    date.getMonth() !== Number(mm) - 1 ||
    date.getDate() !== Number(dd) ||
    Number(hh) > 23 ||
    Number(min) > 59
  )
    return null;
  return `${yyyy}-${mm}-${dd}${withTime ? `T${hh}:${min}` : ''}`;
}

/** Format ISO dates embedded in existing generated descriptions, without altering storage. */
export function formatDateText(value: string): string {
  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  return value.replace(/\b\d{4}-\d{2}-\d{2}\b/g, (date) => formatDisplayDate(date))
    .replace(/\b(\d{1,2}) (Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?) (\d{4})\b/gi,
      (_, day: string, month: string, year: string) => `${day.padStart(2, '0')}/${String(months.indexOf(month.slice(0, 3).toLowerCase()) + 1).padStart(2, '0')}/${year}`);

}
